import { ExternalIdSource, type OrganizationProfileModel } from "api";
import { Transition } from "components/shared/column";
import { authProvidersEnum } from "helpers/enums";
import type { IUserModel } from "helpers/models/user";
import { paths } from "helpers/paths";
import type { RolesModel } from "helpers/roles";
import { ymEvent } from "helpers/system";
import { UmamiManager } from "lib/umami";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getAuthProviderLink } from "sources/account";

type AtiSuIntegrationProps = {
    organization: OrganizationProfileModel;
    profile: IUserModel;
    roles: RolesModel;
};

type AtiSuConnectionState = {
    isOrganizationConnected: boolean;
    isUserConnected: boolean;
};

type ProfileState = {
    organization: OrganizationProfileModel;
    profile: IUserModel;
    roles: RolesModel;
};

function useAtiSuAuthLink(enabled: boolean) {
    const [authLink, setAuthLink] = useState("");
    const [loading, setLoading] = useState(false);
    const [linkError, setLinkError] = useState("");

    useEffect(() => {
        if (!enabled) {
            setAuthLink("");
            setLinkError("");
            setLoading(false);
            return;
        }

        let isMounted = true;
        setAuthLink("");
        setLinkError("");
        setLoading(true);

        getAuthProviderLink(authProvidersEnum.AtiSu)
            .then((link) => {
                if (!isMounted) {
                    return;
                }

                setAuthLink(link ?? "");
                if (!link) {
                    setLinkError("Не удалось получить ссылку для подключения ATI.SU.");
                }
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [enabled]);

    return { authLink, linkError, loading };
}

function AtiSuConnectionDetails({
    isOrganizationConnected,
    isUserConnected,
    organizationName,
}: AtiSuConnectionState & { organizationName?: string }) {
    const connectionStatus = isUserConnected
        ? "Подключено"
        : isOrganizationConnected
          ? "Компания подключена"
          : "Не подключено";

    return (
        <table className="table table-sm col-sm-8 col-lg-6 mb-4">
            <tbody>
                <tr>
                    <td className="noborder">Статус интеграции</td>
                    <td className="noborder">
                        <span className={`badge ${isUserConnected ? "badge-success" : "badge-secondary"}`}>
                            {connectionStatus}
                        </span>
                    </td>
                </tr>
                <tr>
                    <td>Организация CARGO.RUN</td>
                    <td>{organizationName || <>&mdash;</>}</td>
                </tr>
                <tr>
                    <td>Компания ATI.SU</td>
                    <td>{isOrganizationConnected ? "Связана" : "Не связана"}</td>
                </tr>
                <tr>
                    <td>Аккаунт ATI.SU</td>
                    <td>{isUserConnected ? "Связан" : "Не связан"}</td>
                </tr>
            </tbody>
        </table>
    );
}

function AtiSuConnectionAction({
    canConnect,
    isOrganizationConnected,
    isUserConnected,
}: AtiSuConnectionState & {
    canConnect: boolean;
}) {
    const { authLink, linkError, loading } = useAtiSuAuthLink(canConnect);

    const startAuthorization = () => {
        if (!authLink) {
            return;
        }

        ymEvent("ConnectAtiSuIntegration");
        UmamiManager.track("ConnectAtiSuIntegration");
        window.location.assign(authLink);
    };

    if (isUserConnected) {
        return null;
    }

    if (!canConnect) {
        return (
            <div className="alert alert-info col-sm-8 col-lg-6 mb-0" role="status">
                Подключить компанию ATI.SU может только администратор транспортной компании.
            </div>
        );
    }

    return (
        <>
            {linkError && (
                <div className="alert alert-danger col-sm-8 col-lg-6 mb-3" role="alert">
                    {linkError}
                </div>
            )}
            <div>
                <button
                    className="btn btn-sm btn-brand"
                    disabled={loading || !authLink}
                    type="button"
                    onClick={startAuthorization}
                >
                    {isOrganizationConnected ? "Привязать аккаунт ATI.SU" : "Подключить ATI.SU"}
                </button>
            </div>
        </>
    );
}

function AtiSuIntegration({ organization, profile, roles }: AtiSuIntegrationProps) {
    const isUserConnected = profile.loginProviders?.includes(authProvidersEnum.AtiSu) ?? false;
    const isOrganizationConnected = organization.externalIdSources?.includes(ExternalIdSource.AtiSu) ?? false;
    const canConnect = !isUserConnected && (isOrganizationConnected || roles.isTruckingOrganizationAdmin);

    return (
        <Transition dir="right">
            <div className="page-book__show book-show">
                <div className="book-show__head">
                    <Link className="book-show__close close close--sm" to={paths.integration} />
                    <div className="book-show__title">Интеграция с ATI.SU</div>
                </div>
                <div className="book-show__body">
                    <div className="card card-body font-size-14 py-3">
                        <p className="mb-3">
                            Свяжите аккаунт ATI.SU с существующим кабинетом CARGO.RUN, чтобы использовать ATI.SU для
                            входа в систему.
                        </p>
                        <AtiSuConnectionDetails
                            isOrganizationConnected={isOrganizationConnected}
                            isUserConnected={isUserConnected}
                            organizationName={organization.name}
                        />
                        <AtiSuConnectionAction
                            canConnect={canConnect}
                            isOrganizationConnected={isOrganizationConnected}
                            isUserConnected={isUserConnected}
                        />
                    </div>
                </div>
            </div>
        </Transition>
    );
}

const mapStateToProps = (state: { profile: ProfileState }) => ({
    organization: state.profile.organization,
    profile: state.profile.profile,
    roles: state.profile.roles,
});

export default connect(mapStateToProps)(AtiSuIntegration);
